import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  message,
  Card,
} from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type {
  KrybotTemplate,
  KrybotTemplateCreate,
  KrybotTemplateUpdate,
} from '../../../types/krybot';
import styles from './TemplateManagement.module.css';

const { TextArea } = Input;
const { Option } = Select;

interface TemplateFormProps {
  template?: KrybotTemplate | null;
  initialPromptText?: string;
  onSave: () => void;
  onCancel: () => void;
}

export const TemplateForm: React.FC<TemplateFormProps> = ({ template, initialPromptText, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (template) {
      form.setFieldsValue({
        template_key: template.template_key,
        template_type: template.template_type,
        sector: template.sector,
        prompt_text: template.prompt_text,
        metadata: template.metadata ? JSON.stringify(template.metadata, null, 2) : '',
        is_active: template.is_active,
      });
    } else if (initialPromptText) {
      form.setFieldsValue({
        prompt_text: initialPromptText,
      });
    }
  }, [template, initialPromptText, form]);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      const payload: KrybotTemplateCreate | KrybotTemplateUpdate = {
        template_key: values.template_key,
        template_type: values.template_type,
        sector: values.sector,
        prompt_text: values.prompt_text,
        metadata: values.metadata ? JSON.parse(values.metadata) : undefined,
        is_active: values.is_active ?? true,
      };

      if (template) {
        await KrybotService.updateTemplate(template.template_key, payload as KrybotTemplateUpdate);
        message.success('Template updated successfully');
      } else {
        await KrybotService.createTemplate(payload as KrybotTemplateCreate);
        message.success('Template created successfully');
      }

      onSave();
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save template';
      message.error(`Failed to save template: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const validateJson = (_: unknown, value: string) => {
    if (!value || value.trim() === '') {
      return Promise.resolve();
    }
    try {
      JSON.parse(value);
      return Promise.resolve();
    } catch {
      return Promise.reject(new Error('Invalid JSON format'));
    }
  };

  return (
    <div className="template-form">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          is_active: true,
        }}
      >
        <Form.Item
          name="template_key"
          label="Template Key"
          rules={[{ required: true, message: 'Please enter template key' }]}
        >
          <Input placeholder="e.g., chat.prime.intro" disabled={!!template} />
        </Form.Item>

        <Form.Item
          name="template_type"
          label="Template Type"
          rules={[{ required: true, message: 'Please enter template type' }]}
        >
          <Input placeholder="e.g., chat, system, user" />
        </Form.Item>

        <Form.Item name="sector" label="Sector (Optional)">
          <Input placeholder="e.g., finance, manufacturing" />
        </Form.Item>

        <Form.Item
          name="prompt_text"
          label="Prompt Text"
          rules={[{ required: true, message: 'Please enter prompt text' }]}
        >
          <TextArea
            rows={12}
            placeholder="Enter the prompt template text. Use {{variableName}} for variables."
          />
        </Form.Item>

        <Card title="Metadata (JSON)" size="small" style={{ marginBottom: 16 }}>
          <Form.Item
            name="metadata"
            rules={[{ validator: validateJson }]}
          >
            <TextArea
              rows={6}
              className="json-editor"
              placeholder='{"author": "system", "version": "1.0", ...}'
            />
          </Form.Item>
        </Card>

        <Form.Item name="is_active" valuePropName="checked" label="Active">
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
              {template ? 'Update' : 'Create'} Template
            </Button>
            <Button icon={<CloseOutlined />} onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

